import React, { useRef } from 'react';
import {
  TextField,
  FormControl,
  FormHelperText,
  Typography,
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import { useField } from 'formik';
import { useFocusOnError } from '../../shared/hooks';

const getStyles = (theme) => ({
  title: {
    userSelect: 'none',
    fontWeight: 700,
    fontFamily: 'Source Sans Pro',
    color: theme.palette.text.main,
    fontSize: 16,
    [theme.breakpoints.up('xs')]: { fontSize: 16 },
    [theme.breakpoints.up('sm')]: { fontSize: 16 },
    [theme.breakpoints.up('md')]: { fontSize: 18 },
    [theme.breakpoints.up('lg')]: { fontSize: 20 },
    [theme.breakpoints.up('xl')]: { fontSize: 20 },
  },
});

const InputTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'smallFont',
})(({ smallFont }) => ({
  backgroundColor: '#fff',
  color: '#016fd0',
  fontWeight: 'bold',
  border: '3px solid #f0f0f0 !important',
  borderRadius: '0px',
  // padding: '4px',
  fontFamily: 'Source Sans Pro !important',
  '&:focus, &:hover': {
    border: 'none',
  },
  '& .Mui-focused': {
    border: 'none',
    '& fieldset, & fieldset:hover': {
      border: 'none',
    },
  },
  '& .MuiInput-underline': {
    '&:hover': {
      '&:before, &:after': {
        border: 'none',
      },
    },
    '&:before, &:after': {
      border: 'none',
    },
  },
  '& .MuiInputLabel-formControl': {
    color: '#6f6f6f',
    fontWeight: 'bold',
    margin: '4px',
    fontSize: '15px !important',
    textTransform: 'uppercase',
    paddingRight: '2px',
    backgroundColor: '#fff',
    zIndex: 0,
    '&.Mui-focused': {
      color: '#000',
      fontSize: '1rem',
    },
  },
  '& textarea': {
    fontSize: smallFont ? '12px !important' : '15px !important',
  },
}));

const TextArea = ({
  name,
  label,
  placeholder,
  smallFont,
  maxLength,
  disabled,
  subTitle,
  externalTitle,
}) => {
  const [field, meta, fieldProps] = useField(name);
  const theme = useTheme();
  const classes = getStyles(theme);

  const handleChange = (e) => {
    fieldProps.setValue(e.target.value);
  };

  const fieldRef = useRef();
  useFocusOnError({ fieldName: name, fieldId: `form-field-${name}` });

  return (
    <FormControl fullWidth margin="dense">
      {externalTitle && (
        <>
          <Typography sx={classes.title}>{label}</Typography>
          <CustomBoldText>{subTitle}</CustomBoldText>
        </>
      )}
      <InputTextField
        label={externalTitle ? '' : label}
        placeholder={placeholder}
        InputLabelProps={{ htmlFor: `form-field-${name}` }}
        id={`form-field-${name}`}
        value={field.value}
        onChange={handleChange}
        multiline
        rows={4}
        disabled={disabled}
        variant="outlined"
        ref={fieldRef}
        inputProps={{
          'data-testid': `input-${name}`,
          maxLength: maxLength || 500,
        }}
        smallFont={smallFont}
      />
      {meta?.touched && meta?.error && (
        <CustomFormHelperText error>{meta?.error}</CustomFormHelperText>
      )}
    </FormControl>
  );
};

export default TextArea;

const CustomBoldText = styled('div')({
  fontSize: '15px',
  fontWeight: 'bold',
});

const CustomFormHelperText = styled(FormHelperText)({
  marginLeft: '14px',
  marginRight: '14px',
});
